/**
 * Value helpers for the ONE-ROUND combined finding+attribute labeling mode
 * (worklist `label_schema.type === "single_select_with_attributes"`, head-CT
 * reader study).
 *
 * Storage contract — ONE DynamoDB label row per finding (label_key = finding
 * display_name). The backend computes completion as `len(labels)` vs
 * `stats.total_labels = studies × findings`, so we must NOT write extra rows.
 * The single row's `label_value` carries both the call and (when Present) the
 * attributes:
 *   - "Absent" | "Cannot be assessed"  → plain string
 *   - Present                          → JSON `{"call":"Present","attributes":{…}}`
 *
 * Backward-compat: single_select rows store a plain call string ("Present" /
 * "Absent" / "Cannot be assessed"). Any value NOT starting with "{" is a plain
 * call, so the chest/renal/omni path is untouched.
 */

import { logger } from "../../utils/logger";

export type AttrMap = Record<string, string[]>;
/** condition → attrName → allowed options (worklist attribute_definitions). */
export type AttributeDefinitions = Record<string, Record<string, string[]>>;
export type AttributeCardinalityMode = "single_select" | "multi_select";
/** condition → attrName → cardinality (worklist attribute_cardinality). */
export type AttributeCardinality = Record<string, Record<string, AttributeCardinalityMode>>;

export type FindingValueParseError = "malformed_json" | "invalid_attributes";

export interface ParsedFindingValue {
  call: string | undefined;
  attributes: AttrMap;
  error: FindingValueParseError | null;
}

function parseFailure(
  error: FindingValueParseError,
  value: string,
  cause?: unknown,
): ParsedFindingValue {
  logger.warn("labeling_finding_value_parse_failed", {
    error,
    label_value: value,
    cause: cause == null ? undefined : String(cause),
  });
  return { call: value, attributes: {}, error };
}

/** Parse the combined finding value without collapsing a corrupt attribute map into "empty". */
export function parseFindingValue(value: string | undefined | null): ParsedFindingValue {
  if (value == null || value === "") return { call: undefined, attributes: {}, error: null };
  if (!value.startsWith("{")) return { call: value, attributes: {}, error: null };

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (cause) {
    return parseFailure("malformed_json", value, cause);
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return parseFailure("malformed_json", value);
  }

  const record = parsed as { call?: unknown; attributes?: unknown };
  const call = typeof record.call === "string" ? record.call : value;
  if (record.attributes == null) return { call, attributes: {}, error: null };
  if (typeof record.attributes !== "object" || Array.isArray(record.attributes)) {
    return parseFailure("invalid_attributes", value);
  }

  return { call, attributes: record.attributes as AttrMap, error: null };
}

/** The finding call encoded in a stored label_value (JSON `.call` or the raw string). */
export function parseCall(value: string | undefined | null): string | undefined {
  return parseFindingValue(value).call;
}

/** The attributes encoded in a stored label_value (only a Present JSON has any). */
export function parseAttributes(value: string | undefined | null): AttrMap {
  return parseFindingValue(value).attributes;
}

/**
 * Build the label_value to persist for a given call.
 * `combined` is true only for the single_select_with_attributes worklist; when
 * false, Present serializes to the plain "Present" string (single_select parity).
 */
export function buildFindingValue(call: string, attributes: AttrMap, combined: boolean): string {
  if (combined && call === "Present") {
    return JSON.stringify({ call: "Present", attributes: attributes ?? {} });
  }
  return call;
}

/** Every defined attribute for a condition has ≥1 selected option. */
export function attributesFilled(
  defs: Record<string, string[]> | undefined,
  attrs: AttrMap,
): boolean {
  const names = Object.keys(defs ?? {});
  return names.every((a) => (attrs[a] ?? []).length > 0);
}

/**
 * Is one finding's read complete (for case-completion gating)?
 *  - not combined: any recorded call completes it (existing single_select rule).
 *  - combined + Absent/Cannot-be-assessed: complete (attributes are N-A).
 *  - combined + Present: complete only when all its attributes are filled.
 */
export function isFindingComplete(
  value: string | undefined,
  defs: Record<string, string[]> | undefined,
  combined: boolean,
): boolean {
  if (value == null) return false;
  if (!combined) return true;
  const parsed = parseFindingValue(value);
  if (parsed.error) return false;
  if (parsed.call !== "Present") return true;
  return attributesFilled(defs, parsed.attributes);
}

/**
 * Attribute dimensions a reader may legitimately mark MORE THAN ONE option on. Everything else is
 * select-one, and the panel enforces that by REPLACING the selection rather than appending.
 *
 * This is the backward-compatible fallback for existing Head CT worklists.
 * New worklists should carry `label_schema.attribute_cardinality`, allowing
 * device-specific attributes to declare cardinality without frontend edits.
 *
 * Motivation: `label_schema.attribute_definitions` in the worklists is a bare
 * `{finding: {dimension: [options]}}` map with no cardinality metadata, so the panel cannot infer this.
 * 70 cells (4.3% of select-one cells) had been stored multi-valued on ordinal or mutually-exclusive
 * dimensions — Laterality 32, Size 26, Severity 7, Fracture alignment 4, Herniation status 1.
 */
export const MULTI_SELECT_ATTRIBUTES: ReadonlySet<string> = new Set([
  "Brain lobe",
  "Facial bone location",
]);
